"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Clock, X, AlertTriangle } from "lucide-react";

// Le timer LBC démarre au clic "Marquer envoyé" sur une tâche LBC manuelle
// (property dont LBC est la seule source). Durée 3min30 à 6min aléatoire.
// À l'expiration : beep + badge disparaît automatiquement, l'envoi suivant
// est débloqué sans action requise du commercial.

interface Timer {
  id: string;
  userId: string;
  interactionId: string;
  startedAt: string;
  durationSeconds: number;
  expiresAt: string;
  cancelledAt: string | null;
}

// Polling DB toutes les 15s pour synchro inter-onglets (création d'un timer
// dans un autre onglet par ex.). Le countdown local tourne en 1s.
const POLL_INTERVAL_MS = 15000;
// Durée d'affichage du message d'avertissement quand l'envoi est bloqué.
const WARNING_DISPLAY_MS = 6000;

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Beep généré via Web Audio API (évite de charger un .mp3).
function playBeep() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(1100, ctx.currentTime);
      gain2.gain.setValueAtTime(0.3, ctx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc2.start();
      osc2.stop(ctx.currentTime + 0.5);
    }, 600);
  } catch {
    // Audio bloqué (autoplay policy) — silencieux
  }
}

// Events custom dispatchés depuis handleMarkSent.
// - "lbc-timer-started" : timer créé avec succès → force le badge à fetch
//   immédiatement (sinon il faut attendre le polling 15s pour qu'il apparaisse).
// - "lbc-timer-blocked" : tentative bloquée par un timer actif → affiche un
//   avertissement inline orange sous le countdown.
export const LBC_TIMER_STARTED_EVENT = "lbc-timer-started";
export const LBC_TIMER_BLOCKED_EVENT = "lbc-timer-blocked";
export interface LbcTimerBlockedDetail {
  remainingSeconds: number;
}

export default function LbcTimerBadge() {
  const [timer, setTimer] = useState<Timer | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const beepedRef = useRef<string | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTimer = useCallback(async () => {
    try {
      const res = await fetch("/api/lbc-timer", { credentials: "same-origin" });
      if (!res.ok) return;
      const data = await res.json();
      setTimer(data);
    } catch {
      // réseau ko, on réessaiera au prochain poll
    }
  }, []);

  // Polling périodique pour synchro inter-onglets
  useEffect(() => {
    fetchTimer();
    const id = setInterval(fetchTimer, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchTimer]);

  // Tick countdown chaque 1s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // À l'expiration du timer : beep + retrait immédiat du badge. Le commercial
  // n'a rien à confirmer — l'expiration débloque automatiquement le prochain
  // envoi côté API (le check `expiresAt > NOW()` du POST /api/lbc-timer
  // n'inclut plus ce timer).
  useEffect(() => {
    if (!timer) return;
    const remainingMs = new Date(timer.expiresAt).getTime() - now;
    if (remainingMs <= 0 && beepedRef.current !== timer.id) {
      beepedRef.current = timer.id;
      playBeep();
      setTimer(null);
    }
  }, [timer, now]);

  // Écoute les events custom pour réagir immédiatement (sans attendre le
  // polling) : timer démarré → refresh, envoi bloqué → warning inline.
  useEffect(() => {
    const startedHandler = () => {
      fetchTimer();
    };
    const blockedHandler = (e: Event) => {
      const detail = (e as CustomEvent<LbcTimerBlockedDetail>).detail;
      const sec = detail?.remainingSeconds ?? 0;
      const mm = Math.floor(sec / 60);
      const ss = sec % 60;
      setWarningMessage(
        `Attends ${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")} avant le prochain envoi LBC`,
      );
      fetchTimer();
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = setTimeout(() => setWarningMessage(null), WARNING_DISPLAY_MS);
    };
    window.addEventListener(LBC_TIMER_STARTED_EVENT, startedHandler);
    window.addEventListener(LBC_TIMER_BLOCKED_EVENT, blockedHandler);
    return () => {
      window.removeEventListener(LBC_TIMER_STARTED_EVENT, startedHandler);
      window.removeEventListener(LBC_TIMER_BLOCKED_EVENT, blockedHandler);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [fetchTimer]);

  const handleCancel = useCallback(async () => {
    if (!timer) return;
    if (!confirm("Annuler ce timer ? À utiliser uniquement si tu n'as pas envoyé le message.")) return;
    try {
      const res = await fetch(`/api/lbc-timer/${timer.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel" }),
      });
      if (res.ok) {
        setTimer(null);
        beepedRef.current = null;
        toast.info("Timer annulé");
      } else {
        toast.error("Impossible d'annuler");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  }, [timer]);

  // Rien à afficher : pas de timer actif ET pas d'avertissement en cours
  if (!timer && !warningMessage) return null;

  // Si avertissement seul (timer expiré entre l'event et maintenant) → on
  // affiche juste la bulle warning sans countdown
  const remainingMs = timer ? new Date(timer.expiresAt).getTime() - now : 0;
  const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));

  // Couleur du badge selon urgence
  let bgClass = "bg-red-500";
  if (remainingSec < 30) bgClass = "bg-yellow-500";
  else if (remainingSec < 120) bgClass = "bg-orange-500";

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 items-end">
      {timer && (
        <>
          <div
            className={`${bgClass} text-white rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[200px]`}
          >
            <Clock className="w-6 h-6" />
            <div className="flex-1">
              <div className="text-xs opacity-90">Timer LBC</div>
              <div className="text-2xl font-mono font-bold">
                {formatRemaining(remainingSec)}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCancel}
            title="Annuler le timer (uniquement si je n'ai pas envoyé)"
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs px-2 py-1.5 rounded shadow flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Annuler
          </button>
        </>
      )}

      {warningMessage && (
        <div className="bg-orange-500 text-white rounded-lg shadow-lg px-4 py-3 flex items-start gap-2 min-w-[200px] max-w-[320px] animate-in fade-in slide-in-from-top-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm font-medium leading-snug">{warningMessage}</div>
        </div>
      )}
    </div>
  );
}
